import type { Event } from "@sentry/react";

// Sentry's ErrorEvent is Event with type: undefined — it's the type used by the
// beforeSend callback, but it is not re-exported from @sentry/react. We define
// the shape locally so sentryBeforeSend matches the beforeSend signature.
type SentryErrorEvent = Event & { type: undefined };

interface AllowedUserShape {
  id?: string;
  role?: string;
  schoolHash?: string;
}

/**
 * Sentry's User type is `{ id?: string; ip_address?: string; email?: string;
 * username?: string; segment?: string; [key: string]: unknown }`. The
 * `[key: string]: unknown` index signature defeats compile-time enforcement
 * of "no extra fields," so this runtime scrub is the actual privacy boundary.
 *
 * Note: event.extra, event.contexts, event.tags, and event.breadcrumbs are NOT
 * scrubbed here. Callers must never pass PII in those envelopes — breadcrumb
 * capture at the source is already disabled via breadcrumbsIntegration + beforeBreadcrumb.
 */
function scrubUser(user: unknown): AllowedUserShape {
  if (!user || typeof user !== "object") return {};
  const u = user as Record<string, unknown>;
  return {
    id: typeof u.id === "string" ? u.id : undefined,
    role: typeof u.role === "string" ? u.role : undefined,
    schoolHash: typeof u.schoolHash === "string" ? u.schoolHash : undefined,
  };
}

export function sentryBeforeSend(event: SentryErrorEvent): SentryErrorEvent {
  if (event.user) {
    event.user = scrubUser(event.user) as typeof event.user;
  }
  return event;
}
